/**
 * @file keyboard-keys.ts
 * @description GCOME_AP 磁轴键盘按键矩阵（改键）功能实现。
 *
 * 遵循 KeyboardKeysFeature 规范，提供按键矩阵的读取/写入能力。
 *
 * 协议细节（参见 docs/果来微科技_磁轴键盘AP通讯协议.md）：
 * - 按键矩阵定义区：每板载 512*4 字节，支持 8 层按键定义（默认层 + FN1~FN7），
 *   每层 512 字节：前 128 键 × 3 字节（Type/Code0/Code1），尾部为 G 键定义区。
 *   当前 SDK 四层语义模型仅使用前 4 层：
 *     默认层 → win（platform='win' / 非 FN）
 *     FN1   → win-fn（platform='win' / FN）
 *     FN2   → mac（platform='mac' / 非 FN）
 *     FN3   → mac-fn（platform='mac' / FN）
 *   （FN4~FN7 不参与 SDK 层语义映射。）
 *   GCOME_AP 支持在任意系统直接编辑各层配置，层选择完全由调用方显式指定，
 *   不做当前运行系统（macOS/Windows）的自动推断。
 * - 命令：
 *   - 0x07 GetDefaultKeyMatrix — 读取出厂默认矩阵（灯光功能用于反查 LED 索引 → HID）
 *   - 0x08 GetUseKeyMatrix    — 读取当前使用矩阵
 *   - 0x09 SetUseKeyMatrix    — 写入当前使用矩阵
 * - 每键 3 字节格式：Type（按键类型）+ Code0 + Code1（按类型解释）。
 *
 * 本实现把 GCOME_AP 的 3 字节矩阵条目原样解析为 keyAttr（type 直接采用 GCOME 协议原生 Type：
 * 0x00/0x10/0x20/0x21/0x22/0x23/0x24/0x30/0x40/0x50/0x60/0x70/0x80/0x90-0x95/0xe0/0xf0/0xf1/0xf2），
 * 结构化字段（modifyKey/mouse/macro/colWheel 等）按协议解释，code0/code1 一律随 keyAttr.code 保留，
 * 保证读-改-写可无损回环（同一 keyAttr 对任意芯片/协议独立，不再做 CH585M 风格类型码映射）。
 */

import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { KeyboardKeysFeature } from '../../../../features/keyboard-keys/contract'
import type { GcomeApKeyboardDevice } from '../../../../devices/keyboard/gcome-ap/device'
import type { platform } from '../../../../types/config'
import type { WasdSwapReport } from '../../../../types/keyboardPerfCfgGcomeAp'
import type {
  keyAttr,
  keyMapItem,
  LayerKeysMap,
  Macro,
  mouseKey,
  macro as MacroRef,
} from '../../../../types/keyboardKeys'
import type { DksSlotInfo, MtSlotInfo, TglSlotInfo } from '../../../../types/keyboardKeys'
import { GCOME_AP_CMD, GCOME_AP_REPORT } from '../../protocols/cmd'
import { calculateGcomeApAddress } from '../../protocols/address'
import {
  DKS_SLOT_COUNT,
  MT_SLOT_COUNT,
  TGL_SLOT_COUNT,
  createEmptyDksSlot,
  createEmptyMtSlot,
  createEmptyTglSlot,
  encodeDksInfo,
  encodeMtInfo,
  encodeTglInfo,
  parseDksInfo,
  parseMtInfo,
  parseTglInfo,
  readDksInfoBlock,
  readInfoBlock,
  readMtInfoBlock,
  readTglInfoBlock,
  writeDksInfoBlock,
  writeMtInfoBlock,
  writeTglInfoBlock,
} from '../../utils/advanced-keys'
import {
  MACRO_COUNT,
  MACRO_POINTER_AREA_SIZE,
  encodeGcomeApMacroBlock,
  parseGcomeApMacroByIndex,
  readMacroBlock,
  readMacroNames,
  readMacroPlayModes,
  syncMacroPlayModeToKeys,
  writeMacroBlock,
  writeMacroName,
  writeMacroPlayMode,
} from '../../utils/macro'

/** 单层按键矩阵大小（128 键 × 3 字节 + 48 字节 G 键 + 预留）。 */
const LAYER_SIZE = 512

/** 单个按键占用的字节数。 */
const KEY_SIZE = 3

/** 每层按键总数。 */
const KEY_COUNT = 128

/** 单帧最大数据长度。 */
const FRAME_LEN = GCOME_AP_REPORT.payloadSize

/** 高级改键矩阵类型：DKS(0x90) / TGL(0x91) / MT(0x92) / RS(0x93) / SOCD(0x94) / OKS(0x95)。 */
const ADVANCED_MATRIX_TYPES = new Set<number>([0x90, 0x91, 0x92, 0x93, 0x94, 0x95])

/** 判断矩阵类型是否为高级改键（存在独立信息区 slot 数据）。 */
function isAdvancedMatrixType(type: number): boolean {
  return ADVANCED_MATRIX_TYPES.has(type)
}

/** WASD ↔ 方向键互换覆盖的 SDK 语义层数（win/win-fn/mac/mac-fn）。 */
const SWAP_WASD_LAYER_COUNT = 4

/** WASD 物理键 keywords → HID usage 码。 */
const WASD_KEYWORDS: Record<string, number> = {
  W: 0x1a,
  A: 0x04,
  S: 0x16,
  D: 0x07,
}

/** 方向键物理键 keywords → HID usage 码。 */
const ARROW_KEYWORDS: Record<string, number> = {
  '↑': 0x52,
  '←': 0x50,
  '↓': 0x51,
  '→': 0x4f,
}

/** WASD 定义位（HID usage）→ 互换后的方向键 HID usage。 */
const WASD_HID_CODES: Record<number, number> = {
  0x1a: 0x52, // W ↔ ↑
  0x04: 0x50, // A ↔ ←
  0x16: 0x51, // S ↔ ↓
  0x07: 0x4f, // D ↔ →
}

/**
 * @file keyboard-keys.ts
 * @author Rapoo Device SDK Team
 * @date 2026-08-06
 * @description GCOME_AP 键位功能实现
 */
export class GcomeApKeyboardKeys extends KeyboardKeysFeature {
  private get gcomeDevice(): GcomeApKeyboardDevice {
    return this.device as unknown as GcomeApKeyboardDevice
  }

  /** 读取当前选中的板载配置分组（GetBase 偏移 0）。 */
  private async getActiveProfile(): Promise<number> {
    const res = await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.getBase,
      data: new Array(FRAME_LEN).fill(0),
    })
    return res.response.data[0] ?? 0
  }

  /**
   * SDK 层语义（win / win-fn / mac / mac-fn）→ GCOME_AP 物理层序号。
   * - win    → 默认层（0）
   * - win-fn → FN1（1）
   * - mac    → FN2（2）
   * - mac-fn → FN3（3）
   * GCOME_AP 支持在任意系统直接编辑各层配置，platform 缺省时按 win 层处理。
   */
  private layerIndex(isFN?: boolean, platform?: platform): number {
    if (platform === 'mac') return isFN ? 3 : 2
    return isFN ? 1 : 0
  }

  /**
   * 分帧读取当前板载某一层完整按键矩阵（512 字节）。
   * 地址 = 板载偏移 + 层序号 × 512，每帧 56 字节递增。
   * @param command 矩阵命令：0x08 GetUseKeyMatrix（当前使用）/ 0x07 GetDefaultKeyMatrix（出厂默认）
   */
  private async readLayerBlock(
    profile: number,
    layer: number,
    command: number = GCOME_AP_CMD.getUseKeyMatrix,
  ): Promise<Uint8Array> {
    const base = calculateGcomeApAddress(command, profile)
    const start = base + layer * LAYER_SIZE
    const chunks: Uint8Array[] = []
    const maxFrames = Math.ceil(LAYER_SIZE / FRAME_LEN)

    for (let i = 0; i < maxFrames; i++) {
      const frameLen = Math.min(FRAME_LEN, LAYER_SIZE - i * FRAME_LEN)
      const res = await this.gcomeDevice.sendRawApCommand({
        command,
        address: start + i * FRAME_LEN,
        data: new Array(frameLen).fill(0),
      })
      const chunk = res.response.data
      chunks.push(chunk)
      if (chunk.length < frameLen) break
    }

    const result = new Uint8Array(LAYER_SIZE)
    let offset = 0
    for (const chunk of chunks) {
      if (offset >= LAYER_SIZE) break
      const len = Math.min(chunk.length, LAYER_SIZE - offset)
      result.set(chunk.slice(0, len), offset)
      offset += len
    }
    return result
  }

  /**
   * 获取指定层的按键总数（不读写硬件，只统计布局中存在的键位）。
   */
  getLayerKeyCount(_isFN?: boolean, _platform?: platform): number {
    const layoutKeys = this.device.manifest.layout.keys
    let count = 0
    for (const row of layoutKeys) {
      for (const key of row) {
        if (key.key >= 0 && key.key < KEY_COUNT) count++
      }
    }
    return count
  }

  /**
   * 获取当前层键位映射。
   * 返回 LayoutKey.key（固件矩阵/LED 索引）对应的 keyMapItem 列表。
   */
  async getLayerKeysMap(isFN?: boolean, platform?: platform): Promise<LayerKeysMap> {
    const profile = await this.getActiveProfile()
    const block = await this.readLayerBlock(profile, this.layerIndex(isFN, platform))
    const layoutKeys = this.device.manifest.layout.keys
    const result: LayerKeysMap = []

    for (const row of layoutKeys) {
      for (const key of row) {
        const index = key.key
        if (!Number.isInteger(index) || index < 0 || index >= KEY_COUNT) continue
        const base = index * KEY_SIZE
        const keyAttr = this.disposeRead(block[base], block[base + 1], block[base + 2])
        result.push({ index, keyAttr })
      }
    }

    return result
  }

  /**
   * 【流式】获取层键映射表。
   * 每批并发解析 batchSize 个键位后 yield。
   */
  async *getLayerKeysMapStream(
    isFN?: boolean,
    batchSize: number = 18,
    platform?: platform,
  ): AsyncIterable<LayerKeysMap> {
    const layerMap = await this.getLayerKeysMap(isFN, platform)
    for (let i = 0; i < layerMap.length; i += batchSize) {
      yield layerMap.slice(i, i + batchSize)
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  /**
   * 设置单个按键的映射（3 字节写入当前板载对应层）。
   *
   * 若该键位当前为高级改键（DKS/MT/TGL/RS/SOCD/OKS）而本次写入不再是高级类型
   * （即恢复默认键/覆盖为普通键），会先清空旧条目引用的信息区 slot 数据——
   * 矩阵绑定指向的信息区数据一并清理，避免残留孤儿 slot。
   */
  async setLayerKeysMap(keyItem: keyMapItem, isFN?: boolean, platform?: platform): Promise<void> {
    const { index } = keyItem
    if (!Number.isInteger(index) || index < 0 || index >= KEY_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `按键索引必须是 0-${KEY_COUNT - 1}，当前为 ${index}。`,
      )
    }

    const profile = await this.getActiveProfile()
    const layer = this.layerIndex(isFN, platform)

    // 从高级改键恢复/覆盖为普通键：先清空旧条目引用的信息区 slot
    const nextType = keyItem.keyAttr.type ?? 0x00
    if (!isAdvancedMatrixType(nextType)) {
      await this.clearAdvancedSlotIfLeaving(profile, layer, index)
    }

    const base = calculateGcomeApAddress(GCOME_AP_CMD.setUseKeyMatrix, profile)
    const offset = base + layer * LAYER_SIZE + index * KEY_SIZE
    const bytes = this.disposeSend(keyItem.keyAttr)

    await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.setUseKeyMatrix,
      address: offset,
      data: Array.from(bytes),
    })
  }

  /**
   * 若指定键位当前为高级改键，清空其引用的信息区 slot 数据。
   * 仅读取该键位 3 字节（不读整层），用于恢复/覆盖高级改键前清理孤儿数据。
   */
  private async clearAdvancedSlotIfLeaving(
    profile: number,
    layer: number,
    index: number,
  ): Promise<void> {
    const base = calculateGcomeApAddress(GCOME_AP_CMD.getUseKeyMatrix, profile)
    const offset = base + layer * LAYER_SIZE + index * KEY_SIZE
    const res = await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.getUseKeyMatrix,
      address: offset,
      data: [0, 0, 0],
    })
    const rawType = res.response.data[0]
    const slotIndex = res.response.data[1]
    if (!isAdvancedMatrixType(rawType)) return
    await this.clearAdvancedSlot(rawType, slotIndex)
  }

  // ==================== 协议编解码 ====================

  /**
   * 读取：GCOME_AP 矩阵 3 字节 → SDK keyAttr。
   * type 直接采用 GCOME 协议原生 Type（不映射到其他芯片的语义类型码）；
   * 结构化字段按协议解释，code0/code1 一律随 keyAttr.code 保留，读-改-写无损回环。
   * @param rawType 矩阵 Type
   * @param code0   第 2 字节（按 Type 解释）
   * @param code1   第 3 字节（按 Type 解释）
   */
  private disposeRead(rawType: number, code0: number, code1: number): keyAttr {
    const data = {} as keyAttr
    data.type = rawType

    switch (rawType) {
      case 0x00:
        // 无效/禁用键
        break
      case 0x10:
        // 键盘按键：Code0 = HID 修饰位图，Code1 = HID usage code
        if (code0 || code1) {
          data.modifyKey = code0
          data.code = code1 ? [code1] : []
        }
        break
      case 0x20: {
        // 鼠标按键：Code0 = bit 映射（L/M/R/K4/K5）
        const parsedMouseKey: mouseKey = [
          (code0 >> 0) & 0x01,
          (code0 >> 1) & 0x01,
          (code0 >> 2) & 0x01,
          (code0 >> 3) & 0x01,
          (code0 >> 4) & 0x01,
        ]
        if (parsedMouseKey.some((v) => v)) {
          data.mouse = {
            mouseKey: parsedMouseKey,
            x: 0,
            y: 0,
            colWheelNum: 0,
            rowWheelNum: 0,
          }
        }
        data.code = [code0, code1]
        break
      }
      case 0x21:
        // 鼠标滚轮：Code0 = 修饰位图，Code1 = 方向（0x01 上/0xff 下）
        data.colWheel = {
          type: 0,
          number: code1 === 0xff ? -1 : 1,
          time: 0,
        }
        data.code = [code0, code1]
        break
      case 0x22:
        // 鼠标摆动：Code0 = 修饰位图，Code1 = 方向（0x01 / 0xff）
        data.rowWheel = {
          type: 0,
          number: code1 === 0xff ? -1 : 1,
          time: 0,
        }
        data.code = [code0, code1]
        break
      case 0x30:
        // 消费类：Code0 = 低位，Code1 = 高位（合并为 uint16）
        if (code0 || code1) data.code = [code0 | (code1 << 8)]
        break
      case 0x40:
        // 系统类：Code0 = 电源/睡眠/唤醒（Code_h 恒 0x00）
        if (code0) data.code = [code0]
        break
      case 0x70: {
        // 宏按键：Code0 = 宏索引，Code1 = 输出模式（Outtime）
        const macroRef: MacroRef = {
          index: code0,
          loop: code1,
        }
        data.macro = macroRef
        break
      }
      case 0x90:
        // DKS 键：Code0 = 数据区索引
        data.dksIndex = code0
        data.code = [code0, code1]
        break
      case 0x91:
        // TGL 键：Code0 = 数据区索引
        data.tglIndex = code0
        data.code = [code0, code1]
        break
      case 0x92:
        // MTK 键：Code0 = 数据区索引，Code1 = 触发延迟（10ms step）
        data.mtIndex = code0
        data.code = [code0, code1]
        break
      case 0xf0:
        // 功能键(Fn)：Code0 = FuncL，Code1 = FuncH
        if (code0 || code1) {
          data.code = [code0]
          data.confIndex = code1
        }
        break
      case 0x23:
      case 0x24:
      case 0x50:
      case 0x60:
      case 0x80:
      case 0x93:
      case 0x94:
      case 0x95:
      case 0xe0:
      case 0xf1:
      case 0xf2:
        // 无独立结构化模型的协议类型（鼠标 X/Y、火力键、Exe、Web、键盘滚轮、
        // RS/SOCD/OKS、G 键、手柄）：code0/code1 原样保留
        data.code = [code0, code1]
        break
    }

    return data
  }

  /**
   * 写入：SDK keyAttr → GCOME_AP 矩阵 3 字节。
   * 调用方直接传 GCOME 协议原生 Type（0x10/0x20/0x30/0x40/0x70/0xf0/0x90 等），
   * SDK 按 Type 原样写入；各 Type 的字段提取规则与 disposeRead 对称，
   * 保证读-改-写无损回环。
   */
  private disposeSend(data: keyAttr): Uint8Array {
    const bytes = new Uint8Array(KEY_SIZE)

    const rawType = data.type ?? 0x00
    let code0 = 0
    let code1 = 0

    switch (rawType) {
      case 0x00:
        // 禁用
        break
      case 0x10:
        // 键盘按键：Code0 = 修饰位图，Code1 = HID code
        code0 = data.modifyKey ?? 0
        code1 = data.code?.[0] ?? 0
        break
      case 0x20: {
        // 鼠标按键：Code0 = bit 位图（L/M/R/K4/K5）
        const mouseKey = data.mouse?.mouseKey
        if (mouseKey) {
          if (mouseKey[0]) code0 |= 0x01
          if (mouseKey[1]) code0 |= 0x02
          if (mouseKey[2]) code0 |= 0x04
          if (mouseKey[3]) code0 |= 0x08
          if (mouseKey[4]) code0 |= 0x10
        } else {
          // 读回透传的位图原样写回
          code0 = data.code?.[0] ?? 0
        }
        code1 = data.code?.[1] ?? 0
        break
      }
      case 0x21:
        // 鼠标滚轮：Code1 = 方向（0x01 上 / 0xff 下）
        code0 = data.code?.[0] ?? 0
        code1 =
          data.colWheel != null
            ? (data.colWheel?.number ?? 1) < 0
              ? 0xff
              : 0x01
            : (data.code?.[1] ?? 0)
        break
      case 0x22:
        // 鼠标摆动：Code1 = 方向（0x01 / 0xff）
        code0 = data.code?.[0] ?? 0
        code1 =
          data.rowWheel != null
            ? (data.rowWheel?.number ?? 1) < 0
              ? 0xff
              : 0x01
            : (data.code?.[1] ?? 0)
        break
      case 0x23:
        // 鼠标 X/Y：Code0 = X code，Code1 = Y code
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x24:
        // 火力键：Code0 = Fire_delay，Code1 = Fire_count
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x30:
        // 消费类：code[0] 为 uint16 小端（CodeL/CodeH）
        code0 = (data.code?.[0] ?? 0) & 0xff
        code1 = ((data.code?.[0] ?? 0) >> 8) & 0xff
        break
      case 0x40:
        // 系统类：Code0 = 电源/睡眠/唤醒
        code0 = data.code?.[0] ?? 0
        break
      case 0x50:
        // 一键打开应用：Code0 = App index
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x60:
        // 一键上网：Code0 = Web index
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x70:
        // 宏按键：Code0 = 宏索引，Code1 = Outtime
        code0 = data.macro?.index ?? data.code?.[0] ?? 0
        code1 = data.macro?.loop ?? data.code?.[1] ?? 0
        break
      case 0x80:
        // 键盘滚轮：Code0 = Func_type，Code1 = Code
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x90:
        // DKS：Code0 = 数据区索引（优先索引绑定字段）
        code0 = data.dksIndex ?? data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x91:
        // TGL：Code0 = 数据区索引
        code0 = data.tglIndex ?? data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x92:
        // MTK：Code0 = 数据区索引，Code1 = 触发延迟（10ms step）
        code0 = data.mtIndex ?? data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0x93:
      case 0x94:
      case 0x95:
        // RS / SOCS / OKS：Code0 = 绑定 index
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0xe0:
        // G 键：Code0 = G_index（0-15）
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      case 0xf0:
        // 功能键(Fn)：Code0 = FuncL，Code1 = FuncH
        code0 = data.code?.[0] ?? 0
        code1 = data.confIndex ?? data.code?.[1] ?? 0
        break
      case 0xf1:
      case 0xf2:
        // 经典手柄 / Xbox 手柄：Code0 = 功能码，Code1 = 参数
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
        break
      default:
        code0 = data.code?.[0] ?? 0
        code1 = data.code?.[1] ?? 0
    }

    bytes[0] = clampByte(rawType, 0, 255)
    bytes[1] = clampByte(code0, 0, 255)
    bytes[2] = clampByte(code1, 0, 255)
    return bytes
  }

  // ==================== 宏 ====================

  /**
   * 获取宏列表（轻量：含 index/name/loop，不含动作包）。
   * 与通用宏语义一致：本质是宏名字列表——列表项由 App Define 宏名字表驱动
   * （创建宏时预设宏名落盘后即可在列表看到），未命名宏不出现；
   * 动作经 getMacroByIndex 按列表 index 读取。
   */
  async getMacroList(): Promise<Macro[]> {
    const [names, playModes] = await Promise.all([
      readMacroNames(this.gcomeDevice),
      readMacroPlayModes(this.gcomeDevice),
    ])
    return names
      .map((name, index) => ({ name: name || `宏${index}`, index, loop: playModes[index] }))
      .filter((macro) => macro.name !== `宏${macro.index!}`)
  }

  /** 流式获取宏列表（整块读取一次，按批 yield）。 */
  async *getMacroListStream(batchSize = 8): AsyncIterable<Macro[]> {
    const list = await this.getMacroList()
    for (let i = 0; i < list.length; i += batchSize) {
      yield list.slice(i, i + batchSize)
    }
  }

  /** 获取指定索引的宏（名字/播放方式来自 App Define 宏元数据表，动作来自 2K 宏区）。 */
  async getMacroByIndex(index: number): Promise<Macro> {
    if (!Number.isInteger(index) || index < 0 || index >= MACRO_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `宏索引必须是 0-${MACRO_COUNT - 1}，当前为 ${index}。`,
      )
    }
    const [pointerArea, names, playModes] = await Promise.all([
      readInfoBlock(this.gcomeDevice, GCOME_AP_CMD.getMacro, MACRO_POINTER_AREA_SIZE),
      readMacroNames(this.gcomeDevice),
      readMacroPlayModes(this.gcomeDevice),
    ])
    const ptr = pointerArea[index * 2] | (pointerArea[index * 2 + 1] << 8)
    if (ptr === 0 && !names[index]) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `宏 ${index} 未定义。`)
    }
    if (ptr === 0) {
      // 已创建（有名字）但尚未写入动作的宏 → 空动作，无需读整块 2K
      return { name: names[index] || `宏${index}`, index, loop: playModes[index], package: [] }
    }
    const block = await readMacroBlock(this.gcomeDevice)
    const macro = parseGcomeApMacroByIndex(block, index)
    return {
      ...macro,
      name: names[index] || macro.name,
      loop: playModes[index],
    }
  }

  /**
   * 设置指定索引的宏（读-改-写回整块）。
   * - **删除语义由名字决定**（与通用宏语义一致）：空名/占位 `宏N` = 删除，
   *   清除名字与播放方式记录；携带真实名字（含空动作） = 创建/更新，
   *   名字落盘后即可在 getMacroList 看到（createMacro/renameMacro 行为）。
   * - 空动作（创建/删除）走轻量路径：只写该宏 2 字节指针置 0（1 帧），
   *   不读不写 2K 整块；空动作宏跳过键位同步（无动作无触发意义）。
   * - 有动作时读-改-写整块，`macro.loop`（0-255）写 App Define 播放方式表，
   *   并同步所有板载 4 层矩阵中引用该宏的键位（type=0x70 Code0=index 的 Code1=loop）。
   */
  async setMacroByIndex(macro: Macro, index: number): Promise<void> {
    if (!Number.isInteger(index) || index < 0 || index >= MACRO_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `宏索引必须是 0-${MACRO_COUNT - 1}，当前为 ${index}。`,
      )
    }

    const actions = macro.package ?? []
    if (actions.length === 0) {
      // 轻量路径：仅清空该宏指针（2 字节），其他宏不受影响
      await this.gcomeDevice.sendRawApCommand({
        command: GCOME_AP_CMD.setMacro,
        address: index * 2,
        data: [0, 0],
      })
    } else {
      const block = await readMacroBlock(this.gcomeDevice)
      const next = encodeGcomeApMacroBlock(block, macro, index)
      await writeMacroBlock(this.gcomeDevice, next)
    }

    const name = macro.name?.trim()
    const isDeleted = !name || name === `宏${index}`

    if (isDeleted) {
      await writeMacroName(this.gcomeDevice, index, '')
      await writeMacroPlayMode(this.gcomeDevice, index, 0xff)
    } else {
      await writeMacroName(this.gcomeDevice, index, name)
      if (macro.loop != null && Number.isInteger(macro.loop)) {
        const outtime = Math.max(0, Math.min(255, macro.loop))
        await writeMacroPlayMode(this.gcomeDevice, index, outtime)
        if (actions.length > 0) {
          await syncMacroPlayModeToKeys(this.gcomeDevice, index, outtime)
        }
      }
    }
  }

  // ==================== 高级改键数据区列表 ====================

  /**
   * 获取 DKS 高级改键数据区列表（每板载 32 slot）。
   * 键盘矩阵 0x90 仅保存本列表的 index，配置数据存储在独立 DKS 信息区。
   */
  async getDksList(): Promise<DksSlotInfo[]> {
    const block = await readDksInfoBlock(this.gcomeDevice)
    return parseDksInfo(block)
  }

  /** 获取指定索引的 DKS 数据。 */
  async getDksByIndex(index: number): Promise<DksSlotInfo> {
    const list = await this.getDksList()
    const item = list[index]
    if (!item)
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `DKS 索引 ${index} 超出范围 0-${DKS_SLOT_COUNT - 1}。`,
      )
    return item
  }

  /** 设置指定索引的 DKS 数据（读-改-写回整区）。 */
  async setDksByIndex(dks: DksSlotInfo, index: number): Promise<void> {
    if (!Number.isInteger(index) || index < 0 || index >= DKS_SLOT_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `DKS 索引必须是 0-${DKS_SLOT_COUNT - 1}，当前为 ${index}。`,
      )
    }
    await this.setDksList([{ ...dks, index }])
  }

  /**
   * 批量写入 DKS 列表（整区读一次 → 按 index 合并 → 写一次）。
   * 快照还原等场景避免对每个 slot 重复整区 RMW。
   */
  async setDksList(list: DksSlotInfo[]): Promise<void> {
    if (!Array.isArray(list) || list.length === 0) return
    const block = await readDksInfoBlock(this.gcomeDevice)
    const items = parseDksInfo(block)
    for (const dks of list) {
      const index = dks.index
      if (!Number.isInteger(index) || index < 0 || index >= DKS_SLOT_COUNT) continue
      items[index] = { ...dks, index }
    }
    await writeDksInfoBlock(this.gcomeDevice, encodeDksInfo(items))
  }

  /**
   * 获取 MTK 双键互绑数据区列表（每板载 32 slot：tap=单击 / hold=长按绑定）。
   * 键盘矩阵 0x92 仅保存本列表的 index。
   */
  async getMtList(): Promise<MtSlotInfo[]> {
    const block = await readMtInfoBlock(this.gcomeDevice)
    return parseMtInfo(block)
  }

  /** 获取指定索引的 MTK 数据。 */
  async getMtByIndex(index: number): Promise<MtSlotInfo> {
    const list = await this.getMtList()
    const item = list[index]
    if (!item)
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `MTK 索引 ${index} 超出范围 0-${MT_SLOT_COUNT - 1}。`,
      )
    return item
  }

  /** 设置指定索引的 MTK 数据（读-改-写回整区）。 */
  async setMtByIndex(mt: MtSlotInfo, index: number): Promise<void> {
    if (!Number.isInteger(index) || index < 0 || index >= MT_SLOT_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `MTK 索引必须是 0-${MT_SLOT_COUNT - 1}，当前为 ${index}。`,
      )
    }
    await this.setMtList([{ ...mt, index }])
  }

  /**
   * 批量写入 MT 列表（整区读一次 → 按 index 合并 → 写一次）。
   */
  async setMtList(list: MtSlotInfo[]): Promise<void> {
    if (!Array.isArray(list) || list.length === 0) return
    const block = await readMtInfoBlock(this.gcomeDevice)
    const items = parseMtInfo(block)
    for (const mt of list) {
      const index = mt.index
      if (!Number.isInteger(index) || index < 0 || index >= MT_SLOT_COUNT) continue
      items[index] = { ...mt, index }
    }
    await writeMtInfoBlock(this.gcomeDevice, encodeMtInfo(items))
  }

  /**
   * 获取 TGL 高级改键数据区列表（每板载 32 slot）。
   * 键盘矩阵 0x91 仅保存本列表的 index。
   */
  async getTglList(): Promise<TglSlotInfo[]> {
    const block = await readTglInfoBlock(this.gcomeDevice)
    return parseTglInfo(block)
  }

  /** 获取指定索引的 TGL 数据。 */
  async getTglByIndex(index: number): Promise<TglSlotInfo> {
    const list = await this.getTglList()
    const item = list[index]
    if (!item)
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `TGL 索引 ${index} 超出范围 0-${TGL_SLOT_COUNT - 1}。`,
      )
    return item
  }

  /** 设置指定索引的 TGL 数据（读-改-写回整区）。 */
  async setTglByIndex(tgl: TglSlotInfo, index: number): Promise<void> {
    if (!Number.isInteger(index) || index < 0 || index >= TGL_SLOT_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `TGL 索引必须是 0-${TGL_SLOT_COUNT - 1}，当前为 ${index}。`,
      )
    }
    await this.setTglList([{ ...tgl, index }])
  }

  /**
   * 批量写入 TGL 列表（整区读一次 → 按 index 合并 → 写一次）。
   */
  async setTglList(list: TglSlotInfo[]): Promise<void> {
    if (!Array.isArray(list) || list.length === 0) return
    const block = await readTglInfoBlock(this.gcomeDevice)
    const items = parseTglInfo(block)
    for (const tgl of list) {
      const index = tgl.index
      if (!Number.isInteger(index) || index < 0 || index >= TGL_SLOT_COUNT) continue
      items[index] = { ...tgl, index }
    }
    await writeTglInfoBlock(this.gcomeDevice, encodeTglInfo(items))
  }

  /**
   * 清空指定高级改键类型对应的信息区 slot 数据。
   * 由 setLayerKeysMap 在「高级改键 → 普通键」恢复/覆盖时调用，防止信息区残留孤儿 slot。
   */
  private async clearAdvancedSlot(rawType: number, slotIndex: number): Promise<void> {
    if (rawType === 0x90) {
      // DKS
      const block = await readDksInfoBlock(this.gcomeDevice)
      const items = parseDksInfo(block)
      items[slotIndex] = createEmptyDksSlot(slotIndex)
      await writeDksInfoBlock(this.gcomeDevice, encodeDksInfo(items))
      return
    }
    if (rawType === 0x91) {
      // TGL
      const block = await readTglInfoBlock(this.gcomeDevice)
      const items = parseTglInfo(block)
      items[slotIndex] = createEmptyTglSlot(slotIndex)
      await writeTglInfoBlock(this.gcomeDevice, encodeTglInfo(items))
      return
    }
    // 0x92 MT / 0x93 RS / 0x94 SOCD / 0x95 OKS 共用 MT 信息区
    const block = await readMtInfoBlock(this.gcomeDevice)
    const items = parseMtInfo(block)
    items[slotIndex] = createEmptyMtSlot(slotIndex)
    await writeMtInfoBlock(this.gcomeDevice, encodeMtInfo(items))
  }

  // ==================== WASD ↔ 方向键互换（改键层面） ====================

  /**
   * 从 manifest 布局定位物理键位（keywords）的矩阵索引。
   * @param keyword 物理键标识（如 'W'、'↑'）
   * @returns 矩阵索引（0-127），未找到时返回 undefined
   */
  private locateLayoutKey(keyword: string): number | undefined {
    for (const row of this.device.manifest.layout.keys) {
      for (const key of row) {
        if (key.keywords !== keyword) continue
        if (!Number.isInteger(key.key) || key.key < 0 || key.key >= KEY_COUNT) continue
        return key.key
      }
    }
    return undefined
  }

  /**
   * WASD ↔ 方向键互换（改键层面实现，协议功能变量区无对应开关位）。
   *
   * 以 manifest 布局的物理键位（keywords 'W/A/S/D' 与 '↑/←/↓/→'）为定义位，
   * 遍历全部 SDK 语义层（win/win-fn/mac/mac-fn）的按键矩阵：
   * - 开启（enabled=true）：仅把 WASD 物理键位（当前为无修饰 W/A/S/D 纯按键）改为
   *   对应方向键码（W→↑、A→←、S→↓、D→→），方向键位保持不动；
   * - 关闭（enabled=false）：把 WASD 物理键位（当前为互换后的方向键码）还原为
   *   WASD 码，并修复历史误写的方向键位（仅处理当前为 WASD 码的定义位，写回
   *   对应方向键码）。
   * 目标码与当前矩阵同位置码一致时跳过（幂等）；当前绑定非纯按键（组合键/宏等）
   * 或不在互换目标范围内的定义位一律跳过，不破坏用户手动改键。
   *
   * @param enabled 目标状态：true=开启互换，false=关闭（还原 WASD + 修复方向键位）
   * @returns 写后读回确认的互换报告（用于诊断：若 applied 小于 target 或 mismatches
   *          非空，说明写入被固件忽略/覆盖/矩阵被重置，需抓包确认写入协议）。
   */
  async swapWasdKeys(enabled: boolean): Promise<WasdSwapReport> {
    const profile = await this.getActiveProfile()
    const report: WasdSwapReport = { target: 0, applied: 0, mismatches: [] }
    const baseAddr = calculateGcomeApAddress(GCOME_AP_CMD.setUseKeyMatrix, profile)
    const wasdPositions = Object.entries(WASD_KEYWORDS)
      .map(([keyword, hid]) => ({ index: this.locateLayoutKey(keyword), hid }))
      .filter((pos): pos is { index: number; hid: number } => pos.index !== undefined)
    const arrowPositions = Object.entries(ARROW_KEYWORDS)
      .map(([keyword, hid]) => ({ index: this.locateLayoutKey(keyword), hid }))
      .filter((pos): pos is { index: number; hid: number } => pos.index !== undefined)

    for (let layer = 0; layer < SWAP_WASD_LAYER_COUNT; layer++) {
      const use = await this.readLayerBlock(profile, layer)
      const writes: { index: number; expected: number }[] = []

      // WASD 物理键位：开启=写入方向键码；关闭=还原 WASD 码
      for (const { index, hid } of wasdPositions) {
        const offset = index * KEY_SIZE
        if (use[offset] !== 0x10 || use[offset + 1] !== 0) continue
        const code1 = use[offset + 2]
        // 开启：仅处理仍为 WASD 码的定义位；关闭：仅处理已为方向键码的定义位
        const from = enabled ? hid : WASD_HID_CODES[hid]
        if (code1 !== from) continue
        const expected = enabled ? WASD_HID_CODES[hid] : hid
        writes.push({ index, expected })
      }

      // 关闭时修复历史误写的方向键位（仅处理当前为 WASD 码的定义位，其余不动）
      if (!enabled) {
        for (const { index, hid } of arrowPositions) {
          const offset = index * KEY_SIZE
          if (use[offset] !== 0x10 || use[offset + 1] !== 0) continue
          const code1 = use[offset + 2]
          if (code1 === hid) continue
          const expected = WASD_HID_CODES[code1]
          if (expected === undefined) continue
          writes.push({ index, expected })
        }
      }

      if (writes.length === 0) continue

      for (const { index, expected } of writes) {
        await this.gcomeDevice.sendRawApCommand({
          command: GCOME_AP_CMD.setUseKeyMatrix,
          address: baseAddr + layer * LAYER_SIZE + index * KEY_SIZE,
          data: [0x10, 0, expected],
        })
        report.target++
      }

      // 写后读回确认（区分写入被固件忽略 vs 矩阵被重置）
      const verify = await this.readLayerBlock(profile, layer)
      for (const { index, expected } of writes) {
        const offset = index * KEY_SIZE
        if (
          verify[offset] === 0x10 &&
          verify[offset + 1] === 0 &&
          verify[offset + 2] === expected
        ) {
          report.applied++
        } else {
          report.mismatches.push({
            layer,
            index,
            expected,
            actual: verify[offset + 2],
          })
        }
      }
    }
    return report
  }

  /**
   * 当前使用矩阵是否处于 WASD 互换状态。
   * 以 manifest 布局定位 W 物理键位（keywords 'W'），读取默认层（win）使用矩阵：
   * 该键位为无修饰 ↑（0x52）即视为已互换。
   */
  async isSwapWasdActive(): Promise<boolean> {
    const profile = await this.getActiveProfile()
    const wIndex = this.locateLayoutKey('W')
    if (wIndex === undefined) return false
    const use = await this.readLayerBlock(profile, 0)
    const offset = wIndex * KEY_SIZE
    return use[offset] === 0x10 && use[offset + 1] === 0 && use[offset + 2] === 0x52
  }
}

// ─── 工具函数 ───────────────────────────────────────────────────────────────

function clampByte(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value) || 0))
}
